import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import CacheKeys from '../../../cache/CacheKeys'
import CacheManager from '../../../cache/CacheManager'
import RestApiUtils from '../../../apiclient/moebli/RestApiUtils'
import NACHDetailsPageUI, { formatAccountType, getMissingEmailRequirements } from './NACHDetailsPageUI'

const parseAmountToInput = (val) => {
  if (val === null || val === undefined || val === '') return ''
  const s = String(val).replace(/,/g, '').replace(/₹/g, '').trim()
  if (!s || isNaN(Number(s))) return ''
  return s
}

const toDateInputString = (val) => {
  if (!val) return ''
  const s = String(val).trim()
  if (!s || s === '-') return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return s
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split('/')
    return `${y}-${m}-${d}`
  }
  const d = !isNaN(Number(s)) ? new Date(Number(s)) : new Date(s)
  if (!isNaN(d.getTime())) {
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return ''
}

const getTodayDateInput = () => {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatStage = (stage) => {
  if (!stage) return 'Pending'
  const s = stage.trim().toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

class NACHDetailsPageSF extends Component {
  constructor(props) {
    super(props)
    this.state = {
      account: null,
      mandateList: null,
      errorList: [],
      successMsg: null,
      // Inline row editing
      editingUrn: null,
      editForm: null,
      editLoading: false,
      emailSendingUrn: null,
      // Expandable cards & LRN mapping
      expandedUrns: {},
      loanIdToLrnMap: {},
      // Send Email confirmation popup
      sendEmailModalOpen: false,
      sendEmailModalMandate: null,
      // Stage change popup
      stageModalOpen: false,
      stageModalMandate: null,
      stageModalTargetStage: null,
      stageModalUmrn: '',
      stageModalDate: '',
      stageModalErrorList: [],
      stageModalBusy: false,
      // Downloads
      downloadingUrn: null
    }
  }

  componentDidMount() {
    const context = this.props.context
    this.getAccount(context)
    this.getLoans(context)
    this.getMandates(context)
  }

  componentWillUnmount() {
    if (this.successTimer) {
      clearTimeout(this.successTimer)
      this.successTimer = null
    }
  }

  setSuccessMessage = (msg, durationMs = 5000) => {
    if (this.successTimer) {
      clearTimeout(this.successTimer)
      this.successTimer = null
    }
    this.setState({ successMsg: msg, errorList: [] }, () => {
      this.successTimer = setTimeout(() => {
        this.setState({ successMsg: null })
        this.successTimer = null
      }, durationMs)
    })
  }

  clearSuccessMessage = () => {
    if (this.successTimer) {
      clearTimeout(this.successTimer)
      this.successTimer = null
    }
    this.setState({ successMsg: null })
  }

  getAccount(context) {
    const accountId = this.props.match.params.accountId
    const cacheManager = new CacheManager({
      resource: `/api/su/accounts/${accountId}`,
      cacheKey: CacheKeys.Account ? CacheKeys.Account(accountId) : `Account:${accountId}`,
      callback: this.getAccountCallback,
      ttlInSecs: 600
    })
    cacheManager.getObject2(context)
  }

  getAccountCallback = (response) => {
    if (response && response.account) {
      this.setState({ account: response.account })
    }
  }

  getLoans(context) {
    const accountId = this.props.match.params.accountId
    const cacheKey = CacheKeys.AccountLoans ? CacheKeys.AccountLoans(accountId) : `AccountLoans:${accountId}`
    const cacheManager = new CacheManager({
      resource: `/api/su/accounts/${accountId}/loans`,
      cacheKey,
      callback: this.getLoansCallback,
      ttlInSecs: 600
    })
    cacheManager.getObject2(context)
  }

  getLoansCallback = (response) => {
    if (response && response.loanList) {
      const loanIdToLrnMap = {}
      response.loanList.forEach((loan) => {
        if (loan && loan.loanId) {
          loanIdToLrnMap[loan.loanId] = loan.loanRefNum || loan.loanId
        }
      })
      this.setState({ loanIdToLrnMap })
    }
  }

  getMandates(context) {
    const accountId = this.props.match.params.accountId
    RestApiUtils.get(
      context,
      '/api/su/nach/mandates',
      this.getMandatesCallback,
      null,
      { accountId }
    )
  }

  getMandatesCallback = (response) => {
    const errorList = []
    if (response.errorList && response.errorList.length > 0) {
      response.errorList.forEach((e) => {
        errorList.push(e.message || String(e))
      })
      this.setState({
        mandateList: [],
        errorList
      })
      return
    }

    const list = response.mandateList || []
    const expandedUrns = { ...this.state.expandedUrns }
    list.forEach((m) => {
      if (expandedUrns[m.urn] === undefined) {
        expandedUrns[m.urn] = m.active === true || m.active === 'true'
      }
    })
    const hasAnyExpanded = Object.values(expandedUrns).some(Boolean)
    if (!hasAnyExpanded && list.length > 0) {
      const firstActive = list.find((m) => !m.cancelledDate && (m.stage || '').toLowerCase() !== 'cancelled')
      if (firstActive) {
        expandedUrns[firstActive.urn] = true
      }
    }

    this.setState({
      mandateList: list,
      expandedUrns,
      errorList: []
    })
  }

  onToggleExpand = (urn) => {
    this.setState((prevState) => ({
      expandedUrns: {
        ...prevState.expandedUrns,
        [urn]: !prevState.expandedUrns[urn]
      }
    }))
  }

  onRefreshClick = () => {
    const context = this.props.context
    const accountId = this.props.match.params.accountId
    if (CacheKeys.Account) {
      CacheManager.removeObject(CacheKeys.Account(accountId))
    }
    if (CacheKeys.AccountLoans) {
      CacheManager.removeObject(CacheKeys.AccountLoans(accountId))
    }
    this.clearSuccessMessage()
    this.setState(
      {
        mandateList: null,
        errorList: [],
        editingUrn: null,
        editForm: null,
        stageModalOpen: false,
        sendEmailModalOpen: false
      },
      () => {
        this.getAccount(context)
        this.getLoans(context)
        this.getMandates(context)
      }
    )
  }

  // Row Edit Handlers
  onRowEditClick = (mandate) => {
    const bank = mandate.bankDetails || {}
    this.clearSuccessMessage()
    this.setState({
      editingUrn: mandate.urn,
      editForm: {
        amount: parseAmountToInput(mandate.amount),
        startDate: toDateInputString(mandate.startDate),
        endDate: toDateInputString(mandate.endDate),
        holderName: bank.holderName || '',
        bankName: bank.bankName || '',
        branchName: bank.branchName || '',
        accountNumber: bank.accountNumber || '',
        ifsc: bank.ifsc || '',
        micr: bank.micr || '',
        accountType: (bank.accountType && formatAccountType(bank.accountType) !== '-') ? formatAccountType(bank.accountType) : 'Current Account',
        version: mandate.version
      },
      errorList: []
    })
  }

  onCancelRowEdit = () => {
    this.clearSuccessMessage()
    this.setState({
      editingUrn: null,
      editForm: null,
      editLoading: false
    })
  }

  onEditFormChange = (field, value) => {
    this.setState((prevState) => ({
      editForm: {
        ...prevState.editForm,
        [field]: value
      }
    }))
  }

  onSaveRowClick = (mandate) => {
    if (this.state.editLoading) return
    const { editForm, account } = this.state
    const context = this.props.context

    const errors = []
    if (!editForm.holderName || !editForm.holderName.trim()) errors.push('A/c Holder Name is required')
    if (!editForm.bankName || !editForm.bankName.trim()) errors.push('Bank Name is required')
    if (!editForm.accountNumber || !editForm.accountNumber.trim()) errors.push('Account Number is required')
    if (!editForm.ifsc || !editForm.ifsc.trim()) errors.push('IFSC Code is required')
    const rawAmt = String(editForm.amount || '').replace(/,/g, '').replace(/₹/g, '').trim()
    if (!rawAmt || isNaN(Number(rawAmt)) || Number(rawAmt) <= 0) errors.push('Amount must be greater than 0')

    if (errors.length > 0) {
      this.clearSuccessMessage()
      this.setState({ errorList: errors })
      return
    }

    this.clearSuccessMessage()
    this.setState({ editLoading: true, errorList: [] })

    const cleanAmount = String(parseFloat(rawAmt).toFixed(2))
    const startDateVal = editForm.startDate ? String(editForm.startDate).trim() : null
    const endDateVal = editForm.endDate ? String(editForm.endDate).trim() : null

    const payload = {
      urn: mandate.urn,
      accountId: account ? account.accountId : mandate.accountId,
      amount: cleanAmount,
      startDate: startDateVal,
      endDate: endDateVal,
      holderName: editForm.holderName.trim(),
      bankName: editForm.bankName.trim(),
      branchName: editForm.branchName ? editForm.branchName.trim() : null,
      accountNumber: editForm.accountNumber.trim(),
      ifsc: editForm.ifsc.trim().toUpperCase(),
      micr: editForm.micr ? editForm.micr.trim() : null,
      accountType: editForm.accountType || 'Current Account',
      bankDetails: {
        holderName: editForm.holderName.trim(),
        bankName: editForm.bankName.trim(),
        branchName: editForm.branchName ? editForm.branchName.trim() : null,
        accountNumber: editForm.accountNumber.trim(),
        ifsc: editForm.ifsc.trim().toUpperCase(),
        micr: editForm.micr ? editForm.micr.trim() : null,
        accountType: editForm.accountType || 'Current Account'
      },
      version: editForm.version
    }

    RestApiUtils.put(
      context,
      '/api/su/nach/mandates/bankDetails',
      this.onSaveRowCallback,
      payload
    )
  }

  onSaveRowCallback = (response) => {
    this.setState({ editLoading: false })
    const errorList = []
    if (response.errorList && response.errorList.length > 0) {
      response.errorList.forEach((e) => {
        errorList.push(e.message || String(e))
      })
      this.setState({ errorList })
      return
    }

    const updatedMandate = response.mandate
    let mandateList = this.state.mandateList || []
    if (updatedMandate) {
      mandateList = mandateList.map((m) => (m.urn === updatedMandate.urn ? updatedMandate : m))
    }

    this.setState({
      mandateList,
      editingUrn: null,
      editForm: null
    })

    const mandateToCheck = updatedMandate || (mandateList.find((m) => m.urn === this.state.editingUrn))
    const missing = getMissingEmailRequirements(mandateToCheck)
    if (missing.length === 0) {
      this.setSuccessMessage(
        'Mandate and bank details saved successfully. All required details are complete — you can now click "Send Email" to submit to Worldline.',
        7000
      )
    } else {
      this.setSuccessMessage(
        `Mandate and bank details saved successfully. Note: To enable sending the registration email to Worldline, please also set: ${missing.join(', ')}.`,
        9000
      )
    }
    this.getMandates(this.props.context)
  }

  onSendEmailClick = (mandate) => {
    if (this.state.emailSendingUrn) return
    this.clearSuccessMessage()
    this.setState({
      sendEmailModalOpen: true,
      sendEmailModalMandate: mandate,
      errorList: []
    })
  }

  onSendEmailModalCancel = () => {
    this.setState({
      sendEmailModalOpen: false,
      sendEmailModalMandate: null
    })
  }

  onSendEmailModalConfirm = () => {
    const mandate = this.state.sendEmailModalMandate
    if (!mandate || this.state.emailSendingUrn) return
    const context = this.props.context

    this.clearSuccessMessage()
    this.setState({
      sendEmailModalOpen: false,
      sendEmailModalMandate: null,
      emailSendingUrn: mandate.urn,
      errorList: []
    })

    const payload = {
      urn: mandate.urn,
      stage: 'submitted',
      sendEmail: true,
      submittedDate: getTodayDateInput(),
      version: mandate.version
    }

    RestApiUtils.put(
      context,
      '/api/su/nach/mandates/stage',
      this.onSendEmailCallback,
      payload
    )
  }

  onSendEmailCallback = (response) => {
    this.setState({ emailSendingUrn: null })
    const errorList = []
    if (response.errorList && response.errorList.length > 0) {
      response.errorList.forEach((e) => {
        errorList.push(e.message || String(e))
      })
      this.setState({ errorList })
      return
    }

    const updatedMandate = response.mandate
    let mandateList = this.state.mandateList || []
    if (updatedMandate) {
      mandateList = mandateList.map((m) => (m.urn === updatedMandate.urn ? updatedMandate : m))
    }

    this.setState({
      mandateList,
      editingUrn: null,
      editForm: null
    })
    this.setSuccessMessage('Registration email sent to Worldline successfully. Stage updated to Submitted.')
    this.getMandates(this.props.context)
  }

  // Stage change select handlers
  onStageSelectChange = (mandate, newStage) => {
    const currentStage = (mandate.stage || 'pending').toLowerCase()
    if (currentStage === newStage.toLowerCase()) return

    if (newStage === 'registered') {
      this.setState({
        stageModalOpen: true,
        stageModalMandate: mandate,
        stageModalTargetStage: 'registered',
        stageModalUmrn: mandate.umrn || '',
        stageModalDate: getTodayDateInput(),
        stageModalErrorList: [],
        stageModalBusy: false
      })
    } else if (newStage === 'cancelled') {
      this.setState({
        stageModalOpen: true,
        stageModalMandate: mandate,
        stageModalTargetStage: 'cancelled',
        stageModalUmrn: '',
        stageModalDate: getTodayDateInput(),
        stageModalErrorList: [],
        stageModalBusy: false
      })
    } else if (newStage === 'pending') {
      const context = this.props.context
      this.clearSuccessMessage()
      this.setState({ errorList: [] })
      RestApiUtils.put(
        context,
        '/api/su/nach/mandates/stage',
        this.onDirectStageChangeCallback,
        {
          urn: mandate.urn,
          stage: 'pending',
          version: mandate.version
        }
      )
    }
  }

  onDirectStageChangeCallback = (response) => {
    const errorList = []
    if (response.errorList && response.errorList.length > 0) {
      response.errorList.forEach((e) => {
        errorList.push(e.message || String(e))
      })
      this.setState({ errorList })
      return
    }

    const updatedMandate = response.mandate
    let mandateList = this.state.mandateList || []
    if (updatedMandate) {
      mandateList = mandateList.map((m) => (m.urn === updatedMandate.urn ? updatedMandate : m))
    }
    this.setState({ mandateList })
    this.setSuccessMessage(`Mandate stage updated to ${formatStage(updatedMandate ? updatedMandate.stage : '')}`)
    this.getMandates(this.props.context)
  }

  onStageModalCancel = () => {
    this.setState({
      stageModalOpen: false,
      stageModalMandate: null,
      stageModalErrorList: [],
      stageModalBusy: false
    })
  }

  onStageModalUmrnChange = (val) => {
    this.setState({ stageModalUmrn: val })
  }

  onStageModalDateChange = (val) => {
    this.setState({ stageModalDate: val })
  }

  onStageModalSubmit = () => {
    const { stageModalMandate, stageModalTargetStage, stageModalUmrn, stageModalDate } = this.state
    const context = this.props.context

    const errors = []
    if (stageModalTargetStage === 'registered') {
      if (!stageModalUmrn || !stageModalUmrn.trim()) {
        errors.push('UMRN is required for registered stage')
      } else if (!/^[A-Z0-9]{16,20}$/i.test(stageModalUmrn.trim())) {
        errors.push('UMRN must be a valid 16 to 20-character alphanumeric NPCI reference number')
      }
    }
    if (!stageModalDate) {
      errors.push('Date is required')
    }

    if (errors.length > 0) {
      this.setState({ stageModalErrorList: errors })
      return
    }

    this.clearSuccessMessage()
    this.setState({ stageModalBusy: true, stageModalErrorList: [] })

    const dateStr = String(stageModalDate || '').trim() || getTodayDateInput()
    const payload = {
      urn: stageModalMandate.urn,
      stage: stageModalTargetStage,
      version: stageModalMandate.version
    }

    if (stageModalTargetStage === 'registered') {
      payload.umrn = stageModalUmrn.trim().toUpperCase()
      payload.registeredDate = dateStr
    } else if (stageModalTargetStage === 'cancelled') {
      payload.cancelledDate = dateStr
    }

    RestApiUtils.put(
      context,
      '/api/su/nach/mandates/stage',
      this.onStageModalSubmitCallback,
      payload
    )
  }

  onStageModalSubmitCallback = (response) => {
    this.setState({ stageModalBusy: false })
    const errorList = []
    if (response.errorList && response.errorList.length > 0) {
      response.errorList.forEach((e) => {
        errorList.push(e.message || String(e))
      })
      this.setState({ stageModalErrorList: errorList })
      return
    }

    const updatedMandate = response.mandate
    let mandateList = this.state.mandateList || []
    if (updatedMandate) {
      mandateList = mandateList.map((m) => (m.urn === updatedMandate.urn ? updatedMandate : m))
    }

    this.setState({
      mandateList,
      stageModalOpen: false,
      stageModalMandate: null,
      stageModalErrorList: []
    })
    this.setSuccessMessage(`Mandate stage updated to ${formatStage(updatedMandate ? updatedMandate.stage : '')}`)
    this.getMandates(this.props.context)
  }

  // Download handlers
  downloadMandateFile = (mandate, fileType) => {
    if (this.state.downloadingUrn) return
    const context = this.props.context

    this.clearSuccessMessage()
    this.setState({
      downloadingUrn: mandate.urn,
      errorList: []
    })

    RestApiUtils.get(
      context,
      `/api/su/nach/mandates/${mandate.urn}/${fileType}/fileDownloadSignedUrl`,
      this.onDownloadFileCallback
    )
  }

  onDownloadFileCallback = (response) => {
    this.setState({ downloadingUrn: null })

    const errorList = []
    if (response.errorList && response.errorList.length > 0) {
      response.errorList.forEach((e) => {
        errorList.push(e.message || String(e))
      })
      this.setState({ errorList })
      return
    }

    if (response.signedUrl && response.signedUrl.signedUrl) {
      window.open(response.signedUrl.signedUrl, '_blank')
    } else {
      this.setState({ errorList: ['No download URL returned for requested file'] })
    }
  }

  onDownloadExcelClick = (mandate) => {
    this.downloadMandateFile(mandate, 'excel')
  }

  onDownloadMandateClick = (mandate) => {
    this.downloadMandateFile(mandate, 'mandate')
  }

  render() {
    return (
      <NACHDetailsPageUI
        context={this.props.context}
        account={this.state.account}
        mandateList={this.state.mandateList}
        errorList={this.state.errorList}
        successMsg={this.state.successMsg}
        onRefreshClick={this.onRefreshClick}
        // Expandable cards & LRN mapping
        expandedUrns={this.state.expandedUrns}
        onToggleExpand={this.onToggleExpand}
        loanIdToLrnMap={this.state.loanIdToLrnMap}
        // Inline row editing
        editingUrn={this.state.editingUrn}
        editForm={this.state.editForm}
        editLoading={this.state.editLoading}
        onRowEditClick={this.onRowEditClick}
        onCancelRowEdit={this.onCancelRowEdit}
        onEditFormChange={this.onEditFormChange}
        onSaveRowClick={this.onSaveRowClick}
        // Stage dropdown & actions
        onStageSelectChange={this.onStageSelectChange}
        emailSendingUrn={this.state.emailSendingUrn}
        onSendEmailClick={this.onSendEmailClick}
        // Send email confirmation modal
        sendEmailModalOpen={this.state.sendEmailModalOpen}
        sendEmailModalMandate={this.state.sendEmailModalMandate}
        onSendEmailModalCancel={this.onSendEmailModalCancel}
        onSendEmailModalConfirm={this.onSendEmailModalConfirm}
        // Stage modal
        stageModalOpen={this.state.stageModalOpen}
        stageModalMandate={this.state.stageModalMandate}
        stageModalTargetStage={this.state.stageModalTargetStage}
        stageModalUmrn={this.state.stageModalUmrn}
        stageModalDate={this.state.stageModalDate}
        stageModalErrorList={this.state.stageModalErrorList}
        stageModalBusy={this.state.stageModalBusy}
        onStageModalCancel={this.onStageModalCancel}
        onStageModalUmrnChange={this.onStageModalUmrnChange}
        onStageModalDateChange={this.onStageModalDateChange}
        onStageModalSubmit={this.onStageModalSubmit}
        // Downloads
        onDownloadMandateClick={this.onDownloadMandateClick}
        onDownloadExcelClick={this.onDownloadExcelClick}
      />
    )
  }
}

export default withRouter(NACHDetailsPageSF)
